import { registryComponent } from '@boost-sd/components-registry/registry';
import { VDomToString } from '@boost-sd/core-js';
import { getAppHistory } from '@boost-sd/core-js/history';
import FormatCurrency from '@components/FormatCurrency';
import type { GeneraSettingsType } from '@hooks/useGeneralSettings';
import useTranslation from '@hooks/useTranslation';
import { useValidateBreakpoint } from '@providers/AppProvider/Breakpoint/hooks';
import type { AddToCartState } from '@providers/CartProvider';
import type { FilterSettings } from '@providers/FilterProvider';
import type { ProductListItem as ProductListItemType } from '@providers/ProductListProvider';
import { useProduct } from '@providers/ProductProvider';
import type { CTAButtonType, ProductItemGeneralSettings } from '@providers/ThemeProvider/types';
import { buildProductDetailUrlWithVariant, setWindowLocation } from '@utils';
import classnames from '@utils/classnames';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import type { CTAButtonProps } from '@/features/filter-product-list/product-list/product-item/CallToActionButton';
import CTAButton from '@/features/filter-product-list/product-list/product-item/CallToActionButton';
import IntegrationProductRating from '@/features/filter-product-list/product-list/product-item/IntegrationProductRating';
import InventoryStatus from '@/features/filter-product-list/product-list/product-item/InventoryStatus';
import PopupSelectOption from '@/features/filter-product-list/product-list/product-item/PopupSelectOption';
import ProductImage from '@/features/filter-product-list/product-list/product-item/ProductImage';
import ProductInfo from '@/features/filter-product-list/product-list/product-item/ProductInfo';
import type { ProductItemProps } from '@/features/filter-product-list/product-list/product-item/ProductItem';
import { ProductItemGridViewLayout } from '@/features/filter-product-list/product-list/product-item/ProductItem';
import ProductLabel from '@/features/filter-product-list/product-list/product-item/ProductLabel';
import ProductPrice from '@/features/filter-product-list/product-list/product-item/ProductPrice';
import TranslateWithComponent from '@/features/filter-product-list/product-list/product-item/ProductPrice/TranslateWithComponent';
import type {
  ProductSwatchHandler,
  ProductSwatchProps,
} from '@/features/filter-product-list/product-list/product-item/ProductSwatch';
import ProductSwatch from '@/features/filter-product-list/product-list/product-item/ProductSwatch';
import ProductTitle from '@/features/filter-product-list/product-list/product-item/ProductTitle';
import ProductVendor from '@/features/filter-product-list/product-list/product-item/ProductVendor';

import type { ProductImgPropsMapSettings } from './connectors/connectProductImg';
import type { ProductInfoPropsMapSettings } from './connectors/connectProductInfo';
import { useConnectProductSwatches } from './connectors/connectProductSwatches';

export type ProductListItemProps = {
  product: ProductListItemType;
  productInfoSettings: ProductInfoPropsMapSettings;
  productImgSettings: ProductImgPropsMapSettings;
  hasCollection: GeneraSettingsType['addCollectionToProductUrl'];
  priceSettings: GeneraSettingsType['priceSettings'];
  no_image_url: GeneraSettingsType['no_image_url'];
  current_tags: GeneraSettingsType['current_tags'];
  showVariantImageBasedOnSelectedFilter: FilterSettings['showVariantImageBasedOnSelectedFilter'];
  isShortenUrlParam: FilterSettings['isShortenUrlParam'];
  urlScheme: FilterSettings['urlScheme'];
  filterPrefixParam: FilterSettings['filterPrefixParam'];
  onClick?: () => unknown;
  onRender?: (props: ProductItemProps) => React.JSX.Element;
  autoAspectRatio?: `${number}/${number}`;
  hideOtherLabelsWhenSoldOut?: boolean;
  productItemGeneralSettings: ProductItemGeneralSettings;
};

const ProductItem = ({
  product,
  productInfoSettings,
  productImgSettings,
  hasCollection,
  priceSettings,
  no_image_url,
  current_tags,
  showVariantImageBasedOnSelectedFilter,
  isShortenUrlParam,
  urlScheme,
  filterPrefixParam,
  onClick,
  onRender: customRender,
  autoAspectRatio,
  hideOtherLabelsWhenSoldOut,
  productItemGeneralSettings,
}: ProductListItemProps) => {
  const { t } = useTranslation();

  const mobileBreakpoint = useValidateBreakpoint('mobile');

  const {
    id,
    tags,
    available,
    images_info,
    original_images,
    options_with_values,
    variants,
    variant_id,
    title,
    vendor,
    price_min,
    price_max,
    compare_at_price_max,
    compare_at_price_min,
    handle,
    original_title,
    split_product,
  } = product;

  const isSaleCustom = priceSettings?.saleCondition?.(product);

  const [addToCartState, setAddToCartState] = useState<AddToCartState>({
    isAdding: false,
    success: false,
    fail: false,
  });

  const [isOpenPopup, setIsOpenPopup] = useState<boolean>(false);

  const {
    isSale,
    isSoldOut,
    previewProductImages,
    salePercent,
    saleAmount,
    labelByTag,
    firstImage,
  } = useProduct({ tags, available, images_info, price_min, compare_at_price_min }, no_image_url);

  const productDetailUrl = buildProductDetailUrlWithVariant(
    {
      variants,
      variant_id,
      handle,
      split_product,
    },
    hasCollection,
    current_tags || undefined
  );

  const isProductSale = isSaleCustom !== undefined ? isSaleCustom : isSale;

  const optionName = showVariantImageBasedOnSelectedFilter.replace(`${filterPrefixParam}opt_`, '');
  const isShortenUrl = isShortenUrlParam && urlScheme !== 0;

  const urlParams = new URLSearchParams(getAppHistory().location.search);

  const filterValueToShowVariantImage = urlParams.getAll(
    isShortenUrl ? optionName : showVariantImageBasedOnSelectedFilter
  );

  const selectedVariantImageByFilterOption = useMemo(() => {
    let selectedVariantImageByFilterOption = '';

    filterValueToShowVariantImage.every((value) => {
      const selectedVariantByFilterOption = variants.find((item) =>
        item.merged_options.includes(`${optionName}:${value}`)
      );
      if (selectedVariantByFilterOption?.image) {
        selectedVariantImageByFilterOption = selectedVariantByFilterOption.image;
        return false;
      }
      return true;
    });

    return selectedVariantImageByFilterOption;
  }, [filterValueToShowVariantImage.length, filterValueToShowVariantImage[0]]);

  const [productImgToShow, setProductImgToShow] = useState<Array<string>>(previewProductImages);

  const setVariantProductImgToShow = () => {
    if (selectedVariantImageByFilterOption !== '') {
      if (previewProductImages.includes(selectedVariantImageByFilterOption)) {
        if (previewProductImages.indexOf(selectedVariantImageByFilterOption) === 1) {
          setProductImgToShow(previewProductImages.reverse());
        } else {
          setProductImgToShow(previewProductImages);
        }
      } else {
        // replace first item in array of images to show by variant image
        setProductImgToShow(
          previewProductImages.map((item, index) =>
            index === 0 ? selectedVariantImageByFilterOption : item
          )
        );
      }
    } else {
      setProductImgToShow(previewProductImages);
    }
  };

  const handleClosePopup = () => {
    setIsOpenPopup(false);
  };

  const productSwatchProps = {
    id,
    original_images,
    images_info,
    options_with_values,
  };

  const renderProductSwatchesElement = (place: 'img' | 'info') => {
    const elements: Record<`swatches.${number}`, React.ReactElement<ProductSwatchProps>> = {};

    const wrapperSettings = useConnectProductSwatches(place, productSwatchProps).props;

    const swatchesSettings = wrapperSettings;

    [1, 2, 3].forEach((key) => {
      const swatchHandler = useRef<ProductSwatchHandler<string> | null>(null);

      if (!swatchesSettings?.[`swatches.${key}`]) return;

      const handleHover = (_: string, image?: string) => {
        if (swatchesSettings[`swatches.${key}`].changeProductImageOn === 'hover' && image)
          setProductImgToShow([image]);
      };

      const handleBlur = () => {
        if (swatchesSettings[`swatches.${key}`].changeProductImageOn === 'hover') {
          setVariantProductImgToShow();
        }
      };

      const handleSelect = (_: string, image?: string) => {
        if (swatchesSettings[`swatches.${key}`].changeProductImageOn === 'click') {
          image ? setProductImgToShow([image]) : setVariantProductImgToShow();
        }
      };

      const handleClickOutSide = (selectedValue?: string) => {
        if (selectedValue) {
          swatchHandler.current?.selectOption('');
          setVariantProductImgToShow();
        }
      };

      elements[`swatches.${key}`] = (
        <ProductSwatch<string>
          key={`product-swatch-${key}`}
          {...swatchesSettings[`swatches.${key}`]}
          maxItem={4}
          onHover={handleHover}
          onBlur={handleBlur}
          onSelect={handleSelect}
          onClickOutSide={handleClickOutSide}
          handler={swatchHandler}
          swatchButtonTextKey={`productItem.swatchButtonText${key}`}
          isFullWidth={place === 'info'}
        />
      );
    });

    return elements;
  };

  const renderCustomLabelByTag = () => {
    const { elements: productImgElementsProps } = productImgSettings;

    return labelByTag?.map((item, index) => (
      <ProductLabel
        key={`custom-label-${index}`}
        {...productImgElementsProps.customLabelByTag}
        labelType='customLabelByTag'
        labelText={item}
      />
    ));
  };

  const renderCTAElement = (place: 'img' | 'info') => {
    const elements: {
      qvBtn?: React.ReactElement<CTAButtonProps>;
      selectOptionBtn?: React.ReactElement<CTAButtonProps>;
      goToDetailBtn?: React.ReactElement<CTAButtonProps>;
    } = {};

    const wrapperSettings = place === 'img' ? productImgSettings : productInfoSettings;

    const ctaSettings = wrapperSettings.elements.cta;

    if (ctaSettings) {
      ['qvBtn', 'selectOptionBtn', 'goToDetailBtn'].forEach((key) => {
        if (ctaSettings[key as CTAButtonType]) {
          const { action, displayType } = ctaSettings[key as CTAButtonType] || {};

          let handleOnClick: () => void = () => false;

          let buttonText: string | undefined = undefined;

          if (key === 'qvBtn') {
            handleOnClick = () => {
              window.dispatchEvent(
                new CustomEvent('boost-sd-open-quick-view', {
                  detail: {
                    productData: product,
                    type: 'full',
                  },
                })
              );
            };

            buttonText = 'productItem.qvBtnLabel';
          }

          if (key === 'selectOptionBtn') {
            if (isOpenPopup) return;

            buttonText = 'productItem.atcSelectOptionsLabel';

            if (variants?.length === 1) {
              buttonText = addToCartState?.isAdding
                ? 'productItem.atcAddingToCartBtnLabel'
                : addToCartState?.success
                ? 'productItem.atcAddedToCartBtnLabel'
                : addToCartState?.fail
                ? 'productItem.atcFailedToCartBtnLabel'
                : 'productItem.atcAvailableLabel';

              handleOnClick = () => {
                if (addToCartState?.isAdding) return;
                setAddToCartState((prev) => ({ ...prev, isAdding: true }));
                window.dispatchEvent(
                  new CustomEvent('boost-sd-add-to-cart', {
                    detail: {
                      variantId: variants?.[0]?.id,
                      quantity: 1,
                      onSuccess: () => {
                        setTimeout(() => {
                          setAddToCartState((prev) => ({
                            fail: false,
                            isAdding: false,
                            success: false,
                          }));
                        }, 350);
                        setAddToCartState((prev) => ({
                          ...prev,
                          isAdding: false,
                          success: true,
                        }));
                      },
                      onFail: () => {
                        setTimeout(() => {
                          setAddToCartState((prev) => ({
                            fail: false,
                            isAdding: false,
                            success: false,
                          }));
                        }, 350);
                        setAddToCartState((prev) => ({ ...prev, fail: true, isAdding: false }));
                      },
                    },
                  })
                );
              };
            } else {
              if (action === 'popup') {
                handleOnClick = () =>
                  window.dispatchEvent(
                    new CustomEvent('boost-sd-open-quick-view', {
                      detail: {
                        productData: product,
                        type: 'mini',
                      },
                    })
                  );
              }

              if (action === 'quickAddToCart') {
                if (mobileBreakpoint) {
                  handleOnClick = () =>
                    window.dispatchEvent(
                      new CustomEvent('boost-sd-open-quick-view', {
                        detail: {
                          productData: product,
                          type: 'mini',
                        },
                      })
                    );
                } else {
                  handleOnClick = () => setIsOpenPopup(true);
                }
              }

              if (action === 'productPage') {
                handleOnClick = () => setWindowLocation(productDetailUrl);
              }
            }

            if (isSoldOut) buttonText = 'productItem.soldoutLabel';
          }

          if (key === 'goToDetailBtn') {
            handleOnClick = () => setWindowLocation(productDetailUrl);
            buttonText = 'productItem.viewProductBtnLabel';
          }

          if (displayType === 'icon') buttonText = undefined;

          elements[key as CTAButtonType] = (
            <CTAButton
              disabled={key === 'selectOptionBtn' && isSoldOut}
              key={`${id}-${key}`}
              onClick={handleOnClick}
              {...ctaSettings[key as CTAButtonType]}
              text={buttonText}
              className={key === 'qvBtn' ? classnames('btn-quick-view') : ''}
            />
          );
        }
      });
    }

    return elements;
  };

  const inventoryQuantity = useMemo(() => {
    if (!variants?.length) return null;

    if (variant_id) {
      const productVariant = variants?.find((item) => +item.id === variant_id);
      return productVariant?.inventory_quantity ?? null;
    }

    return variants?.reduce((total, item) => (total += item.inventory_quantity), 0);
  }, [variants, variant_id]);

  const onRender = () => {
    const { elements: productInfoElementsProps } = productInfoSettings;
    const { elements: productImgElementsProps } = productImgSettings;

    const productItemProps: ProductItemProps = {
      id,
      product,
      variants,
      variant_id,
      handle,
      previewProductImages,
      isSale,
      isSoldOut,
      salePercent,
      saleAmount,
      title,
      vendor,
      price_min,
      compare_at_price_min,
      options_with_values,
      productDetailUrl,
      tags,
      images_info,
      original_title,
      original_images,
      onClick,
      productInfo: (
        <ProductInfo
          {...productInfoSettings}
          elements={{
            title: productInfoElementsProps.title && (
              <ProductTitle {...productInfoElementsProps.title} title={title} />
            ),
            price: productInfoElementsProps.price && (
              <ProductPrice
                setting={productInfoElementsProps.price}
                data={{
                  isSale: isProductSale,
                  salePercent,
                  saleAmount,
                  price_min,
                  price_max,
                  compare_at_price_max,
                  compare_at_price_min,
                }}
              />
            ),
            vendor: productInfoElementsProps.vendor && (
              <ProductVendor {...productInfoElementsProps.vendor} vendor={vendor} />
            ),
            inventoryStatus: productInfoElementsProps.inventoryStatus && (
              <InventoryStatus
                {...productInfoElementsProps.inventoryStatus}
                inventory={inventoryQuantity}
                isSoldOut={isSoldOut}
              />
            ),
            rating: productInfoElementsProps.rating ? (
              <IntegrationProductRating key='rating' textAlign={productInfoSettings.textAlign} />
            ) : (
              <></>
            ),
            ...renderProductSwatchesElement('info'),
            ...renderCTAElement('info'),
          }}
        />
      ),
      productImg: (
        <ProductImage
          {...productImgSettings}
          productId={variant_id || id}
          images={productImgToShow}
          altImage={title}
          autoAspectRatio={autoAspectRatio}
          naturalAspectRatio={firstImage && `${firstImage.width}/${firstImage.height}`}
          originalSize={images_info}
          elements={{
            rating: productImgElementsProps.rating ? (
              <IntegrationProductRating key='rating' />
            ) : (
              <></>
            ),
            saleLabel:
              isProductSale &&
              productImgElementsProps.saleLabel &&
              !(isSoldOut && hideOtherLabelsWhenSoldOut) ? (
                <ProductLabel
                  key='sale-label'
                  {...productImgElementsProps.saleLabel}
                  labelType='sale'
                  labelText={
                    <VDomToString vDOM={<FormatCurrency value={saleAmount} />}>
                      {({ html: saleLabelString }) => {
                        return (
                          <TranslateWithComponent
                            translation='productItem.productItemSale'
                            data={{
                              salePercent,
                              saleAmount: saleLabelString,
                            }}
                          />
                        );
                      }}
                    </VDomToString>
                  }
                />
              ) : undefined,
            soldOutLabel:
              isSoldOut && productImgElementsProps.soldOutLabel ? (
                <ProductLabel
                  key='sold-out-label'
                  {...productImgElementsProps.soldOutLabel}
                  labelType='soldout'
                  labelText={t('productItem.productItemSoldOut')}
                />
              ) : undefined,
            customLabelByTag:
              productImgElementsProps.customLabelByTag &&
              !(isSoldOut && hideOtherLabelsWhenSoldOut) ? (
                <Fragment key='custom-label'>{renderCustomLabelByTag()}</Fragment>
              ) : undefined,
            ...renderCTAElement('img'),
            ...renderProductSwatchesElement('img'),
          }}
        />
      ),
      popupSelectOption: isOpenPopup ? (
        <PopupSelectOption
          variants={variants || []}
          optionsWithValue={options_with_values || []}
          handleClosePopupSelectOption={handleClosePopup}
        />
      ) : (
        <></>
      ),
      ...productItemGeneralSettings,
    };

    if (customRender) return customRender(productItemProps);

    return <ProductItemGridViewLayout {...productItemProps} hasCollection={hasCollection} />;
  };

  useEffect(() => {
    setVariantProductImgToShow();
  }, [selectedVariantImageByFilterOption]);

  return onRender();
};

export default registryComponent('ProductItemWidget', ProductItem);
